<template>
  <v-card
    class="pb-3 elevation-0 creator-card"
    rounded="0"
  >
    <EditContent
      v-if="showEditContentDialog"
      :content="selectedContent"
      :kind="kind"
      :topic="!!topic?.landingpage?.slug || !topic?.isSystemGenerated"
      @update="updateText"
      @close="closeEditDialog"
    />
    <OfficeSelectionDialog
      v-if="isMasterView"
      v-model="showOfficeSelectionDialog"
      :initial-selected-offices="selectedOffices.length > 0 ? selectedOffices : undefined"
      :initial-include-head-office="includeHeadOffice"
      :is-social-media-connected="isSocialMediaConnected"
      @confirm="handleOfficeSelectionConfirm"
    />
    <v-row class="d-flex">
      <v-col
        cols="12"
        md="4"
        lg="2"
      >
        <v-select
          v-model="kind"
          :items="availableKinds"
          variant="outlined"
          hide-details
          :disabled="isAnyPartLoading"
          density="compact"
          :label="$t('buttons.content-creator.text-kind')"
          :menu-props="kindMenuProps"
          class="pointer"
          item-title="text"
          item-value="value"
          data-product-fruits="content-creator-text-kind"
          data-testid="content-creator-text-kind-select"
          @update:model-value="handleKindChange(kind)"
          @click="handleKindClick()"
        >
          <template #selection="{ item }">
            <div class="d-flex align-center text-truncate selection-label">
              <v-avatar
                start
                size="20"
              >
                <r-img :src="item.raw.icon" />
              </v-avatar>
              <span class="ml-3">{{ item.raw.text }}</span>
            </div>
          </template>

          <template #item="{ item, props }">
            <v-list-item
              v-bind="!item.raw.needsUpgrade ? props : {}"
              :disabled="item.raw.disabled"
              @click="item.raw.needsUpgrade && goToUpgrade()"
            >
              <template #prepend>
                <v-avatar size="24">
                  <r-img :src="item.raw.icon" />
                </v-avatar>
              </template>

              <template #title>
                <v-list-item-title>
                  {{ item.title }}
                </v-list-item-title>
              </template>

              <template
                #subtitle
                v-if="item.raw.needsUpgrade || item.raw.disabled"
              >
                <v-list-item-subtitle class="needs-upgrade-subtitle">
                  {{ item.raw.inactiveMessage }}
                </v-list-item-subtitle>
              </template>
              <template
                #append
                v-if="item.raw.needsUpgrade"
              >
                <v-icon> mdi-one-up </v-icon>
              </template>
            </v-list-item>
          </template>
        </v-select>
      </v-col>

      <v-col
        cols="12"
        md="4"
        lg="2"
      >
        <v-select
          v-model="topicId"
          :items="topicSelectItems"
          variant="outlined"
          hide-details
          :disabled="isAnyPartLoading"
          density="compact"
          :label="$t('buttons.content-creator.topic')"
          class="pointer"
          item-title="text"
          item-value="value"
          data-product-fruits="content-creator-topic"
          data-testid="content-creator-topic-select"
          @update:model-value="handleTopicChange()"
          @click="handleTopicClick()"
        >
          <template #item="{ item, props }">
            <v-list-subheader v-if="item.raw.header">
              {{ item.raw.header }}
            </v-list-subheader>
            <v-divider v-else-if="item.raw.divider" />
            <v-list-item
              v-else
              v-bind="props"
            />
          </template>
        </v-select>
      </v-col>

      <v-col
        v-if="enabledTargetLinks"
        cols="12"
        md="4"
        lg="2"
      >
        <TargetLinks
          @disable-generate-button="disableGenerateButton = $event"
          @target-link="targetLink = $event"
          @individual-link="individualLink = $event"
          :kind="kind"
          :is-custom-topic="!topic?.isSystemGenerated"
          :has-google-review="!!googleReviewLink"
          :is-loading="isAnyPartLoading"
          :show-upselling="showUpselling"
          :enabledTargetLinks="enabledTargetLinks"
        />
      </v-col>

      <v-col
        v-if="isMasterView"
        cols="12"
        md="4"
        lg="2"
      >
        <v-tooltip
          location="top"
          :disabled="!showUpselling"
        >
          <template #activator="{ props: tooltipProps }">
            <div
              v-bind="tooltipProps"
              class="w-100"
            >
              <v-select
                v-model="publication"
                :items="publicationOptions"
                variant="outlined"
                hide-details
                :disabled="isAnyPartLoading || showUpselling"
                density="compact"
                :label="$t('buttons.content-creator.publication')"
                class="pointer"
                item-title="text"
                item-value="value"
                @update:model-value="handlePublicationChange"
              >
                <template #item="{ item, props }">
                  <v-list-item
                    v-bind="props"
                    :disabled="item.raw.disabled"
                    @click="!item.raw.disabled && handlePublicationItemClick(item)"
                  />
                </template>
              </v-select>
            </div>
          </template>
          <span>
            <div>
              {{ $t('alerts.upselling.upgrade.2') }}
            </div>
          </span>
        </v-tooltip>
      </v-col>

      <v-col
        v-if="imageSourceItems.length > 1"
        cols="12"
        md="4"
        lg="2"
      >
        <ImageSourceSelect
          v-model="selectedImageSourceRaw"
          :image-source-items="imageSourceItems"
          :is-loading="isAnyPartLoading"
          :has-text-kind-image="hasTextKindImage"
          @upgrade="goToUpgrade"
        />
      </v-col>

      <v-col
        cols="12"
        md="4"
        lg="2"
      >
        <v-btn
          class="w-full generate-btn"
          color="primary"
          elevation="0"
          :disabled="isAnyPartLoading || !kind || !topic"
          data-product-fruits="content-creator-generate-content"
          data-testid="content-creator-generate-content-btn"
          @click="generate"
        >
          <!-- TODO: check unsupported prop: dark -->
          <v-icon start> mdi-refresh </v-icon>
          {{ $t('buttons.content-creator.generate-content') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <template v-if="hasTopics">
          <div ref="creator">
            <GeneratedContent
              :kind="kind"
              :has-image="hasImage"
              :generated-content="generatedContent"
              :selected-content="selectedContent"
              :loading-state="loadingState"
              :is-no-image="isNoImage"
              :selected-image-source-type="selectedImageSourceType"
              :selected-image-source-variant="selectedImageSourceVariant"
              :topic-id="topicId"
              :is-stock-photos-ready="isStockPhotosReady"
              @openEditTextDialog="openEditTextDialog"
              @change="handleChange"
              @image-updated="handleImageUpdate"
            />
          </div>
          <div
            class="mt-5 w-full mt-2 d-flex justify-flex-start align-md-center"
            :class="{
              'justify-sm-center ': kind !== postType.newsletter,
              'justify-center': kind === postType.newsletter,
            }"
          >
            <DownloadPost
              btn-class="my-2"
              :save-content="true"
              :text-kind="kind"
              :image="isNoImage ? null : selectedContent?.image?.url"
              :carousel-images="selectedCarouselImages.map((img) => img.url)"
              :image-source="isNoImage ? null : selectedImageSourceType"
              :text="selectedContent?.text"
              :topic="topic?.name"
              :topic-id="topic?.id"
              :url="topic?.landingpage?.url"
              :isNotLicensedForDownload="isNotLicensedForDownload"
              :disabled="isDownloadDisabled"
              :primary="true"
              @contentSaved="$emit('contentSaved')"
            />
            <WhatsAppSharing
              v-if="kind === KindType.whatsappStatus && !showEditContentDialog"
              class="ml-md-2"
              :disabled="isLoadingOrPostNotComplete"
              :image="selectedContent?.image?.url"
              :text="selectedContent?.text"
              :topic="topic?.name"
              :topic-id="topic?.id"
              :text-kind="kind"
              :image-source="selectedImageSourceType"
              :media-type="selectedContent?.image?.type"
              :is-not-licensed-for-download="isNotLicensedForDownload"
              @contentSaved="$emit('contentSaved')"
            />
            <Newsletter
              class="ml-md-2"
              v-else-if="kind === postType.newsletter && !showEditContentDialog"
              :disabled="isLoadingOrPostNotComplete"
              :company="company"
              :return-call-landingpage-url="
                returnCallLandingpage?.customUrl || returnCallLandingpage?.originalUrl
              "
              :content="newsletterContent"
              :is-master-view="isMasterView"
              :target-link="targetLink"
              :individual-link="individualLink"
              :selected-offices="publication === 'individual' ? selectedOffices : []"
              :publication="publication"
              :include-head-office="includeHeadOffice || !isMasterView || publication === 'head'"
              :can-send-preview="canSendNewsletterPreview"
              @contentSaved="$emit('contentSaved')"
              @savedScheduledContent="$emit('savedScheduledContent')"
            >
              <template #button="{ props }">
                <v-btn
                  v-bind="props"
                  class="next-btn ml-2 my-2"
                  :disabled="isLoadingOrPostNotComplete"
                >
                  {{ $t('content-creator.schedule-email.button') }}
                </v-btn>
              </template>
            </Newsletter>
            <SocialMediaSharing
              v-else-if="kind !== KindType.whatsappStatus"
              :content="socialMediaContent"
              :isLicenseRequired="isNoImage ? false : selectedContent?.image?.isLicenseRequired"
              :selectedImageSourceVariant="selectedImageSourceVariant"
              :disabled="isLoadingOrPostNotComplete || !canPostOrShare"
              :selectedOffices="sharingSelectedOffices"
              :publication="publication"
              :include-head-office="includeHeadOffice || !isMasterView || publication === 'head'"
              :can-post-to-instagram="canPostOrShareOnInstagram"
              :company="company"
              @contentSaved="onContentSaved"
              @savedScheduledContent="onScheduledContent"
            />
          </div>
          <GenerationLimits />
        </template>

        <template v-else-if="!isLoadingTopics">
          <info-box
            :info-message="infoMessages.generateContent"
            :link-message="infoMessages.generateContentLink"
            :link-path="'/topics'"
          />
        </template>
      </v-col>
    </v-row>
  </v-card>
</template>
<script>
import * as Sentry from '@sentry/vue'
import { computed } from 'vue'

import InfoBox from '@/components/InfoBox.vue'
import ImageType from '@/lib/enums/ImageType'
import { transformToKebabCase } from '@/lib/kebabCase'
import { isExpertUser } from '@/lib/productTypes.js'
import setQueryParameters from '@/lib/setQueryParameters'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import { uploadToS3 } from '@/lib/uploadToS3'
import brandingMixin from '@/mixins/branding'
import featureMixin from '@/mixins/feature'
import EditContent from '@/modules/contentCreator/creator/EditContent.vue'
import { FilterType } from '@/modules/contentCreator/creator/enums/FilterType'
import { PostType } from '@/modules/contentCreator/creator/enums/PostType'
import { TargetLink } from '@/modules/contentCreator/creator/enums/targetLink'
import { buildUrlWithTracking } from '@/modules/contentCreator/creator/lib/urlWithTracking'
import { hasUtmParams } from '@/modules/contentCreator/creator/lib/utm.js'
import Newsletter from '@/modules/contentCreator/creator/newsletter/Newsletter.vue'
import TargetLinks from '@/modules/contentCreator/creator/TargetLinks.vue'
import COMPANY from '@/modules/settings/companySettings/queries/CompanyData.gql'
import GET_GOOGLE_REVIEW_SETTINGS from '@/modules/settings/emailSettings/queries/GetGoogleReviewSettings.gql'

import CONFIRM_MATERIAL_UPLOAD from '../../adMaterials/ConfirmMaterialUpload.gql'
import REQUEST_MATERIAL_UPLOAD_URL from '../../adMaterials/RequestMaterialUploadUrl.gql'
import UPLOAD_MATERIALS from '../../adMaterials/UploadMaterials.gql'
import DownloadPost from './DownloadPost.vue'
import KindType, { KindTypeIcon } from './enums/KindType'
import GeneratedContent from './GeneratedContent.vue'
import GenerationLimits from './GenerationLimits.vue'
import ImageSourceSelect from './ImageSourceSelect.vue'
import { partMapper } from './lib/partMapper'
import OfficeSelectionDialog from './OfficeSelectionDialog.vue'
import CONFIRM_USER_IMAGE_UPLOAD from './queries/ConfirmUserImageUpload.gql'
import GENERATE_IMAGE from './queries/generateImage.gql'
import GENERATE_TEXT from './queries/generateText.gql'
import GET_ACTIVE_TOPICS from './queries/getActiveTopics.gql'
import GET_AD_MATERIALS from './queries/getAdMaterials.gql'
import GET_GENERATED_IMAGES from './queries/getGeneratedImages.gql'
import RETURN_CALL_LANDINGPAGE from './queries/getLandingpageByType.gql'
import GET_LAST_GENERATED_TEXT from './queries/getLastGeneratedText.gql'
import GET_MATERIALS from './queries/getMaterials.gql'
import GET_SOCIAL_MEDIA_CONNECTIONS from './queries/getSocialMediaConnections.gql'
import GET_STOCK_PHOTOS from './queries/getStockPhotos.gql'
import GET_USER_IMAGES from './queries/getUserImages.gql'
import REQUEST_USER_IMAGE_UPLOAD_URL from './queries/RequestUserImageUploadUrl.gql'
import UPLOAD_USER_IMAGE from './queries/uploadUserImage.gql'
import SocialMediaSharing from './SocialMedia/SocialMediaSharing.vue'
import WhatsAppSharing from './WhatsApp/WhatsAppSharing.vue'

// Google Business accepts text-only posts, so these kinds must not carry any
// leftover image state once "No image" is selected.
const googleBusinessKinds = [KindType.gmbNewsPost, KindType.gmbProductDescription]

export default {
  components: {
    TargetLinks,
    Newsletter,
    EditContent,
    GeneratedContent,
    InfoBox,
    DownloadPost,
    GenerationLimits,
    SocialMediaSharing,
    WhatsAppSharing,
    ImageSourceSelect,
    OfficeSelectionDialog,
  },
  mixins: [featureMixin, brandingMixin],
  props: {
    activeTab: {
      type: String,
      default: FilterType.ALL,
    },
  },
  emits: ['contentSaved', 'savedScheduledContent', 'deleteScheduledPost'],
  provide() {
    return {
      canCreateCarouselPost: computed(() => this.canCreateCarouselPost),
      isCarouselMode: computed(() => this.isCarouselMode),
      selectedCarouselImages: computed(() => this.selectedCarouselImages),
      supportCarouselPosts: computed(() => this.supportCarouselPosts),
      handleCarouselImageToggle: this.handleCarouselImageToggle,
      generateContent: this.generate,
      canGeneratePersonalizedImage: computed(() => this.canGeneratePersonalizedImage),
    }
  },
  data() {
    return {
      googleReviewLink: null,
      disableGenerateButton: false,
      maskedTargetLink: null,
      individualLink: '',
      targetLink: TargetLink.landingpage,
      topicFromQuery: null,
      imageIdFromQuery: null,
      loadingState: {
        image: false,
        text: false,
      },
      KindType,
      isStockPhotosReady: false,

      /**
       * Unprocessed config from the feature.
       */
      availableImageSources: [],

      /**
       * The selected dropdown value.
       * e.g. MATERIAL, AI.dall-e
       */
      selectedImageSourceRaw: null,

      /**
       * The original image source, extracted from the raw value.
       * e.g. MATERIAL, AI, STOCK, USER
       */
      selectedImageSourceType: null,

      /**
       * The variant of the image source, extracted from the raw value.
       * e.g. dall-e, flux-pro, flux-dev
       */
      selectedImageSourceVariant: null,

      stockPhotos: {
        page: 1,
        perPage: 20,
      },
      featureConfig: {},
      enabledTextKinds: [],
      enabledTargetLinks: true,
      kind: this.$route.query.kind || KindType.facebookPost,
      topics: [],
      topicId: null,
      generatedContent: {
        image: {},
        text: {
          // Let's set an empty string as default so there is always at least one slide in the carousel for showing the loading spinner
          choices: [''],
        },
      },
      selectedContent: {
        image: {},
        text: '',
      },
      showSocialMediaDialog: false,
      showEditContentDialog: false,
      editTextDialogIndex: null,
      enabledPostingChannels: {},
      selectedCarouselImages: [],
      publication: 'all',
      showOfficeSelectionDialog: false,
      selectedOffices: [],
      includeHeadOffice: false,
      socialMediaConnections: [],
      canSendNewsletterPreview: true,
    }
  },
  computed: {
    canGeneratePersonalizedImage() {
      const isPersonalizedModel = this.selectedImageSourceRaw === 'AI-gemini-flash-personalized'
      const hasAmbassadorImage = !!this.company?.ambassador?.image?.url
      if (isPersonalizedModel && !hasAmbassadorImage && !this.isMasterView) return false
      return true
    },
    showUpselling() {
      return !isExpertUser(this.$auth.user)
    },
    isMasterView() {
      return this.$route.path.startsWith('/master')
    },
    isSocialMediaConnected() {
      return this.socialMediaConnections && this.socialMediaConnections.length > 0
    },
    publicationOptions() {
      const options = [
        {
          text: this.$t('content-creator.publication-options.all-offices'),
          value: 'all',
        },
        {
          text: this.$t('content-creator.publication-options.head-office'),
          value: 'head',
          disabled: !this.isSocialMediaConnected,
        },
        {
          text: this.$t('content-creator.publication-options.individual-selection'),
          value: 'individual',
        },
      ]

      if (this.selectedOffices.length > 0 || this.includeHeadOffice) {
        const totalCount = this.selectedOffices.length + (this.includeHeadOffice ? 1 : 0)
        options[2].text = `${this.$t('content-creator.publication-options.individual-selection')} (${totalCount})`
      }

      return options
    },
    isCarouselMode() {
      return this.selectedCarouselImages.length > 0
    },
    postType() {
      return PostType
    },
    supportCarouselPosts() {
      return [KindType.facebookPost, KindType.instagramPost]
    },
    canCreateCarouselPost() {
      if (!this.supportCarouselPosts.includes(this.kind)) {
        return false
      }

      const enoughGeneratedContent =
        this.generatedContent?.image?.choices.filter(
          (img) => img?.id !== 'fetchMore' && img.id !== 'uploadImage'
        )?.length >= 2
      return (
        this.supportCarouselPosts.includes(this.kind) &&
        this.selectedCarouselImages.length < 20 &&
        enoughGeneratedContent
      )
    },
    isGenerateOrUploadSelected() {
      return (
        this.selectedContent?.image?.id === 'fetchMore' ||
        this.selectedContent?.image?.id === 'uploadImage'
      )
    },
    isDownloadDisabled() {
      return this.isAnyPartLoading || this.isNotLicensedForDownload
    },
    isNotLicensedForDownload() {
      return (
        this.isAnyPartLoading ||
        (this.selectedImageSourceType === ImageType.STOCK &&
          !this.selectedContent?.image?.canBeDownloaded)
      )
    },
    isAnyPartLoading() {
      return Object.values(this.loadingState).some(Boolean)
    },
    topicSelectItems() {
      const categories = this.topics
        .filter((topic) => topic.visibility?.contentCreator === true)
        .map((topic) => {
          return {
            value: topic.id,
            text: topic.name,
            type: topic.landingpage ? topic.landingpage.type : 'TOPIC',
          }
        })
        .reduce(
          (acc, { type, ...rest }) => {
            if (type in acc) {
              acc[type].items.push(rest)
            } else {
              acc.TOOL.items.push({ type, ...rest })
            }
            return acc
          },
          {
            TOOL: { items: [], header: this.$t('labels.content-creator.tools') },
            GUIDE: { items: [], header: this.$t('labels.content-creator.guides') },
            CHECKLIST: { items: [], header: this.$t('labels.content-creator.checklists') },
            TOPIC: { items: [], header: this.$t('labels.content-creator.topics') },
          }
        )

      const slugs = []
      Object.values(categories).forEach(({ items, header }, index) => {
        if (items.length > 0) {
          if (index !== 0) {
            slugs.push({ divider: true })
          }
          slugs.push({ header }, ...items)
        }
      })
      return slugs
    },
    imageSourceItems() {
      const iconMap = {
        MATERIAL: 'mdi-image-multiple',
        STOCK: 'mdi-database',
        USER: 'mdi-upload',
        AI: 'mdi-auto-fix',
        NO_IMAGE: 'mdi-image-off',
      }

      const getTranslation = (imageSource) => {
        if (imageSource === 'USER') return this.$t('content-creator.image-sources.USER')

        return this.$t(`content-creator.image-sources.${imageSource}`, {
          platform: this.readablePlatformName,
        })
      }

      const isDisabled = {
        NO_IMAGE: !this.kindSupportNoImage.includes(this.kind),
      }

      return [...this.availableImageSources, { value: 'NO_IMAGE' }].reduce((acc, conf) => {
        const icon = iconMap[conf.value]

        if (conf.variants?.length > 0) {
          const items = conf.variants.map((variant) => {
            const value = `${conf.value}-${variant.value}`
            return {
              needsUpgrade: variant.needsUpgrade,
              text: getTranslation(value),
              value,
              icon,
              disabled: isDisabled[conf.value] || false,
            }
          })
          return [...acc, ...items]
        }

        return [
          ...acc,
          {
            needsUpgrade: false, // for now we assume top level image sources are available if defined
            text: getTranslation(conf.value),
            value: conf.value,
            icon,
            disabled: isDisabled[conf.value] || false,
          },
        ]
      }, [])
    },
    topic() {
      return this.topics.find(({ id }) => id === this.topicId) || {}
    },
    infoMessages() {
      return {
        generateContent: this.$t('alerts.content-creator.generate-content.info'),
        generateContentLink: this.$t('alerts.content-creator.generate-content.link'),
        publishPostExample: this.$t('alerts.content-creator.social-media.info'),
      }
    },
    socialMediaContent() {
      const hasImage = !this.isNoImage && !this.isGenerateOrUploadSelected
      return {
        text: this.selectedContent.text,
        image: hasImage ? this.selectedContent.image?.url : null,
        imageSource: hasImage ? this.selectedImageSourceType : null,
        modelName: this.isNoImage ? null : this.selectedImageSourceRaw,
        stockPhotoId: this.isNoImage ? null : this.selectedContent.image?.id,
        textKind: this.kind,
        topicId: this.topic?.id,
        topicName: this.topic?.name,
        socialMediaPosts: [],
        carouselImages: this.isNoImage ? [] : this.selectedCarouselImages.map((img) => img.url),
        targetLink: {
          name: this.targetLink,
          link: this.maskedTargetLink,
          custom: this.individualLink,
        },
        isVideo: this.isNoImage ? false : this.selectedContent.image?.isVideo || false,
        mediaType: this.isNoImage ? null : this.selectedContent.image?.type,
        publication: this.publication,
      }
    },
    sharingSelectedOffices() {
      return this.publication === 'individual' ? this.selectedOffices : []
    },
    /**
     * Text kinds that can be posted without an image. Google Business accepts
     * text-only posts, so both GBP kinds belong here.
     */
    kindSupportNoImage() {
      return [
        KindType.facebookPost,
        KindType.linkedinPost,
        KindType.gmbNewsPost,
        KindType.gmbProductDescription,
        KindType.customerMailing,
        KindType.blogPost,
        KindType.websiteText,
        KindType.interview,
        KindType.newsletter,
      ]
    },
    selectedKindAspectRatios() {
      if (!this.kind) return []
      return this.availableKinds.find((kind) => kind.value === this.kind)?.aspectRatios ?? []
    },
    availableKinds() {
      const getAvailableAspectRatios = (kind) => {
        const landscapeKinds = [KindType.websiteText, KindType.blogPost]
        const portraitKinds = [KindType.whatsappStatus]

        const aspectRatios = []
        if (landscapeKinds.includes(kind)) aspectRatios.push('landscape')
        if (portraitKinds.includes(kind)) aspectRatios.push('portrait')

        return aspectRatios
      }

      const disableKind = (kind) => {
        if (this.selectedCarouselImages.length > 1)
          return (
            !this.supportCarouselPosts.includes(kind) &&
            this.$t('content-creator.does-not-support-carousel-posts')
          )

        return false
      }

      const needsUpgrade = (kind) =>
        !(this.enabledTextKinds === 'all' || this.enabledTextKinds.includes(kind))

      const inactiveMessage = (kind) => {
        if (needsUpgrade(kind)) {
          if (kind === KindType.newsletter)
            return this.$t('content-creator.text-kind-upgrade-only-expert')
          else return this.$t('content-creator.text-kind-upgrade')
        }
        return disableKind(kind) ?? ''
      }

      const allowedKindsForMasterView = [
        KindType.facebookPost,
        KindType.instagramPost,
        KindType.linkedinPost,
        KindType.gmbNewsPost,
        KindType.gmbProductDescription,
        KindType.newsletter,
      ]

      const postingFeatureMap = {
        [KindType.facebookPost]: 'postToFacebook',
        [KindType.instagramPost]: 'postToInstagram',
        [KindType.linkedinPost]: 'postToLinkedin',
        [KindType.whatsappStatus]: 'postToWhatsappStatus',
        [KindType.gmbNewsPost]: 'postToGoogleBusiness',
        [KindType.gmbProductDescription]: 'postToGoogleBusiness',
      }

      const kindsToShow = this.isMasterView
        ? Object.keys(KindType).filter((key) => allowedKindsForMasterView.includes(key))
        : Object.keys(KindType)

      return kindsToShow
        .filter((key) => {
          const featureKey = postingFeatureMap[key]
          return featureKey ? !!this.featureConfig[featureKey] : true
        })
        .map((key) => ({
          value: key,
          text: this.$t(`content-creator.text-kind-types.${this.transformToKebabCase(key)}`),
          icon: KindTypeIcon[key],
          needsUpgrade: needsUpgrade(key),
          aspectRatios: getAvailableAspectRatios(key),
          disabled: !!disableKind(key),
          inactiveMessage: inactiveMessage(key),
        }))
    },
    hasTextKindImage() {
      return partMapper[this.kind].includes('image')
    },
    isNoImage() {
      const postsWithImage = [
        this.postType.facebookPost,
        this.postType.instagramPost,
        this.postType.linkedinPost,
        this.postType.facebookAds,
        this.postType.gmbNewsPost,
        this.postType.blogPost,
        this.postType.websiteText,
        this.postType.newsletter,
        this.postType.whatsappStatus,
      ]

      return this.selectedImageSourceRaw === 'NO_IMAGE' || !postsWithImage.includes(this.kind)
    },
    hasImage() {
      return !this.isNoImage && this.generatedContent?.image?.choices?.length > 0
    },
    hasText() {
      return this.generatedContent?.text?.choices?.length > 0
    },
    isLoadingOrPostNotComplete() {
      if (this.isAnyPartLoading) return true
      if (this.isNoImage) return false
      if (this.isGenerateOrUploadSelected) return false
      return (
        this.isAnyPartLoading ||
        (partMapper[this.kind].includes('text') && !this.selectedContent?.text) ||
        (partMapper[this.kind].includes('image') && !this.selectedContent?.image?.url)
      )
    },
    canPostOrShare() {
      const isInstagram = this.kind === KindType.instagramPost
      const isImageChosen =
        (this.selectedContent?.image?.id !== 'fetchMore' &&
          this.selectedContent?.image?.id !== 'uploadImage') ||
        this.selectedCarouselImages.length

      if (isInstagram && !isImageChosen) return false
      return true
    },
    canPostOrShareOnInstagram() {
      return (
        (this.selectedContent?.image?.id !== 'fetchMore' &&
          this.selectedContent?.image?.id !== 'uploadImage') ||
        this.selectedCarouselImages.length
      )
    },
    isLoadingTopics() {
      return this.$apollo.queries.topics.loading
    },
    hasTopics() {
      return this.topics.length > 0
    },
    kindMenuProps() {
      if (this.$vuetify.display.smAndUp) {
        return { maxWidth: 400 }
      }
      return {}
    },
    newsletterContent() {
      return {
        image: this.isNoImage ? null : this.selectedContent?.image?.url,
        imageSource: this.isNoImage ? null : this.selectedImageSourceType,
        text: this.selectedContent.text,
        textKind: this.kind,
        topicId: this.topic?.id,
        url: this.topic?.landingpage?.url,
        topic: this.topic?.landingpage?.slug,
      }
    },
  },
  watch: {
    topicSelectItems: {
      handler(topicsList) {
        if (!topicsList.length) return
        if (this.topicFromQuery) {
          this.topicId = this.topicFromQuery
          this.topicFromQuery = null
          return
        }

        const isSelectedTopicValid = topicsList.some((topic) => topic?.value === this.topicId)
        if (isSelectedTopicValid) return

        const propertyValuationTopic = topicsList.find(
          (topic) => topic?.text === 'Immobilienbewertung'
        )
        if (propertyValuationTopic) {
          this.topicId = propertyValuationTopic.value
          return
        }

        const firstTopic = topicsList.find((topic) => !!topic?.value)
        if (firstTopic) {
          this.topicId = firstTopic.value
        }
      },
    },
    async selectedImageSourceRaw(value) {
      if (!value) return

      const [type, ...variantParts] = value.split('-')
      const variant = variantParts.join('-')
      this.selectedImageSourceType = type
      this.selectedImageSourceVariant = variant || null

      if (value === 'NO_IMAGE') {
        this.selectedCarouselImages = []
        // For newsletter with NO_IMAGE, show the "no material" message
        if (this.kind === KindType.newsletter) {
          this.generatedContent.image = {
            choices: [
              {
                id: 'noImage',
                selectAi: () => this.setSelectedImageSourceRaw(ImageType.AI),
                selectStock: () => this.setSelectedImageSourceRaw(ImageType.STOCK),
              },
            ],
          }
          this.setDefaultSelectedImage()
        } else if (googleBusinessKinds.includes(this.kind)) {
          // Google Business hides the image carousel here, so drop the image state
          // instead of keeping a stale selection around.
          this.resetGeneratedContent('image')
        }
        return
      }

      // Reset stock photos ready state when changing image source
      if (type === 'STOCK') {
        this.isStockPhotosReady = false
      }

      // Need to update the link with different image source
      this.generatedContent.text.choices = this.generatedContent.text?.choices?.map((text) =>
        this.updateLinkInText(text)
      )
      this.selectedCarouselImages = []
      await this.getImages()
    },
    async kind(value) {
      this.resetGeneratedContent('text')
      let imageSourceChanged = false

      // If switching to newsletter, default to NO_IMAGE
      if (value === KindType.newsletter) {
        this.resetGeneratedContent('image')
        this.selectedImageSourceRaw = 'NO_IMAGE'
      } else if (value === KindType.whatsappStatus) {
        const personalizedAiAvailable = this.availableImageSources.some(
          (c) =>
            c.value === ImageType.AI &&
            c.variants?.some((variant) => variant.value === 'gemini-flash-personalized')
        )
        const aiAvailable = this.availableImageSources.some((c) => c.value === ImageType.AI)

        if (
          personalizedAiAvailable &&
          this.selectedImageSourceRaw !== 'AI-gemini-flash-personalized'
        ) {
          this.selectedImageSourceRaw = 'AI-gemini-flash-personalized'
          imageSourceChanged = true
        } else if (aiAvailable) {
          const defaultAiVariant = this.availableImageSources.find(
            (c) => c.value === ImageType.AI
          )?.defaultVariant
          const nextImageSource = defaultAiVariant ? `AI-${defaultAiVariant}` : ImageType.AI

          if (this.selectedImageSourceRaw !== nextImageSource) {
            this.setSelectedImageSourceRaw(ImageType.AI)
            imageSourceChanged = true
          }
        }
      } else {
        const shouldChangeImageSource = !this.kindSupportNoImage.includes(value) && this.isNoImage
        if (shouldChangeImageSource) {
          this.changeToFirstImageSource()
        } else if (this.isNoImage && googleBusinessKinds.includes(value)) {
          // NO_IMAGE stays selected on a Google Business kind. Drop any image left
          // over from the previous kind (e.g. the newsletter "no material"
          // placeholder), which the image carousel no longer renders.
          this.resetGeneratedContent('image')
        }
      }

      // Clear carousel images when switching to a non-carousel kind
      const carouselKinds = [KindType.facebookPost, KindType.instagramPost]
      if (!carouselKinds.includes(value)) {
        this.selectedCarouselImages = []
      }

      if (
        !imageSourceChanged &&
        value !== KindType.newsletter &&
        (this.selectedImageSourceType === 'USER' || this.selectedImageSourceType === 'MATERIAL')
      ) {
        await this.getImages()
      }
    },
    async topicId() {
      this.resetGeneratedContent('text')
      this.resetGeneratedContent('image')

      this.selectedCarouselImages = []

      await this.getImages()
    },
    targetLink() {
      this.updateTextWithCurrentLink()
    },
    individualLink() {
      if (this.targetLink === TargetLink.individualLink) {
        this.updateTextWithCurrentLink()
      }
    },
  },
  async created() {
    this.topicFromQuery = this.$route.query.topicId || null
    this.imageIdFromQuery = this.$route.query.imageId || null
    const feature = await this.getFeature(this.featureNames.CONTENT_CREATOR)
    const {
      enabledTextKinds,
      availableImageSources,
      stockImageLimit,
      postToFacebook,
      postToInstagram,
      postToLinkedin,
      postToGoogleBusiness,
      enabledTargetLinks,
      canSendNewsletterPreview,
    } = feature.config

    this.featureConfig = feature.config
    this.enabledTextKinds = enabledTextKinds
    this.enabledTargetLinks = enabledTargetLinks
    this.canSendNewsletterPreview = canSendNewsletterPreview ?? true
    this.availableImageSources = availableImageSources || []
    // Initialize with default image source - we'll try different sources to find the image
    this.setSelectedImageSourceRaw(this.$route.query.imageSource)
    this.setDefaultSelectedImage()

    this.stockPhotos.limit = stockImageLimit

    this.enabledPostingChannels = {
      [KindType.facebookPost]: postToFacebook,
      [KindType.instagramPost]: postToInstagram,
      [KindType.linkedinPost]: postToLinkedin,
      [KindType.gmbNewsPost]: postToGoogleBusiness,
      [KindType.gmbProductDescription]: postToGoogleBusiness,
    }

    if (this.isCallbackAfterLinkedinAuth()) {
      this.openSocialMediaDialog()
    }
  },
  methods: {
    transformToKebabCase,
    changeToFirstImageSource() {
      this.selectedImageSourceRaw = this.availableImageSources[0].value
    },
    handleKindChange(kind) {
      this.$tracking.event('Content Creator', 'Selected', 'Textkind', kind)
      this.$tracking.heapEvent('text_type_selected', { text_type: kind })
    },
    handleKindClick() {
      this.$tracking.event('Content Creator', 'Clicked', 'Textkind')
    },
    handleTopicChange() {
      this.$tracking.event('Content Creator', 'Selected', 'Topic', this.topic.name)
      this.$tracking.heapEvent('topic_selected', { topic_name: this.topic.name })
    },
    handleTopicClick() {
      this.$tracking.event('Content Creator', 'Clicked', 'Topic')
    },
    handlePublicationChange(value) {
      if (value === 'individual') {
        // Use nextTick to ensure dialog can reopen even if already selecting individual
        this.$nextTick(() => {
          this.showOfficeSelectionDialog = true
        })
      }
      this.$tracking.event('Content Creator', 'Selected', 'Publication', value)
    },
    /**
     * @param {Object} item - The dropdown item that was clicked
     * @param {string} item.value - The publication type value ('all', 'head', or 'individual')
     */
    handlePublicationItemClick(item) {
      if (item.value === 'individual' && this.publication === 'individual') {
        this.openOfficeSelectionDialog()
      }
    },
    openOfficeSelectionDialog() {
      this.showOfficeSelectionDialog = true
    },
    closeOfficeSelectionDialog() {
      this.showOfficeSelectionDialog = false
    },
    handleOfficeSelectionConfirm(data) {
      this.selectedOffices = data.offices
      this.includeHeadOffice = data.includeHeadOffice
      this.showOfficeSelectionDialog = false
    },
    isCallbackAfterLinkedinAuth() {
      return this.$route.query.linkedinAuthCallback
    },

    startLoading(part) {
      this.loadingState[part] = true
    },

    stopLoading(part) {
      this.loadingState[part] = false
    },

    closeConnectDialog() {
      this.showSocialMediaDialog = false
    },
    closeEditDialog() {
      this.editTextDialogIndex = null
      this.showEditContentDialog = false
    },

    onContentSaved() {
      this.selectedCarouselImages = []
      this.$emit('contentSaved')
    },

    onScheduledContent() {
      this.selectedCarouselImages = []
      this.$emit('savedScheduledContent')
    },

    resetGeneratedContent(part = null) {
      if (part === 'text') {
        // Let's set an empty string as default so there is always at least one slide in the carousel for showing the loading spinner
        this.generatedContent.text = { choices: [''] }
        this.setDefaultSelectedText()
      } else if (part === 'image') {
        this.generatedContent.image = {}
        this.setDefaultSelectedImage()
      } else {
        this.generatedContent = {
          image: {},
          text: { choices: [''] },
        }
      }
    },

    updateText(text) {
      this.generatedContent.text.choices = [
        ...this.generatedContent.text.choices.slice(0, this.editTextDialogIndex),
        text,
        ...this.generatedContent.text.choices.slice(this.editTextDialogIndex + 1),
      ]
      this.selectedContent.text = text
    },
    openEditTextDialog(index) {
      this.showEditContentDialog = true
      this.editTextDialogIndex = index
    },
    handleChange(data) {
      if (data.generatedContent) {
        // Update the generated content if provided
        this.generatedContent = data.generatedContent
      }
      this.selectedContent[data.part] = data.content
    },
    handleImageUpdate(imageData) {
      // Update the image in both generatedContent and selectedContent
      if (imageData && this.generatedContent?.image?.choices) {
        const index = this.generatedContent.image.choices.findIndex(
          (img) => img.id === imageData.contentId
        )
        if (index !== -1) {
          // Ensure we have a valid image source type
          if (!this.selectedImageSourceType) {
            // If no source type is set, try to get it from the feature config
            this.setSelectedImageSourceRaw()
          }

          // Update the image in generatedContent
          this.generatedContent.image.choices = [
            ...this.generatedContent.image.choices.slice(0, index),
            {
              ...this.generatedContent.image.choices[index],
              preview: imageData.newPreviewUrl,
              url: imageData.newPreviewUrl,
            },
            ...this.generatedContent.image.choices.slice(index + 1),
          ]

          // Update selectedContent if it's the current image
          if (this.selectedContent.image?.id === imageData.contentId) {
            this.selectedContent.image = {
              ...this.selectedContent.image,
              preview: imageData.newPreviewUrl,
              url: imageData.newPreviewUrl,
            }
          }
        }
      }
    },
    setDefaultSelectedImage() {
      if (this.imageIdFromQuery && this.generatedContent?.image?.choices) {
        const matchingImage = this.generatedContent.image.choices.find(
          (image) => image.fileId === this.imageIdFromQuery
        )
        if (matchingImage) {
          this.selectedContent = {
            ...this.selectedContent,
            image: matchingImage,
          }
          this.imageIdFromQuery = null
          return
        }
      }
      this.selectedContent.image = this.generatedContent?.image?.choices?.[0]
    },
    setDefaultSelectedText() {
      this.selectedContent.text = this.generatedContent?.text?.choices?.[0]
    },
    // TODO: this won't work anymore if we allow to disable hashtags.
    // Instead link should be added through AI genereration.
    addLinkToText: function (text) {
      if (
        [KindType.facebookPost, KindType.linkedinPost, KindType.whatsappStatus].includes(
          this.kind
        ) &&
        ((!this.enabledTargetLinks && this.topic?.landingpage?.url) ||
          (this.targetLink === TargetLink.landingpage && this.topic?.landingpage?.url) ||
          (this.targetLink === TargetLink.landingpage && !this.topic?.isSystemGenerated) ||
          (this.targetLink === TargetLink.customerMainWebsite && this.company?.links?.website) ||
          (this.targetLink === TargetLink.individualLink && this.individualLink) ||
          (this.targetLink === TargetLink.googleReview && this.googleReviewLink) ||
          this.targetLink === TargetLink.landingpagePerOffice ||
          this.targetLink === TargetLink.officeWebsite)
      ) {
        const target =
          this.targetLink && this.targetLink !== TargetLink.noLink ? this.targetLink : 'landingpage'
        this.maskedTargetLink = null

        const maskMap = {
          officeWebsite: `<< ${this.$t('content-creator.generate-text.add-link-to-text-mask.0')} >>`,
          landingpagePerOffice: `<< ${this.$t('content-creator.generate-text.add-link-to-text-mask.1')} >>`,
        }

        if (hasUtmParams(this.individualLink)) {
          maskMap.individualLink = this.individualLink
        }

        const urlMap = {
          landingpage: !this.topic?.isSystemGenerated
            ? this.returnCallLandingpage?.customUrl || this.returnCallLandingpage?.originalUrl
            : this.topic?.landingpage?.url,
          individualLink: this.individualLink,
          customerMainWebsite: this.company?.links?.website,
          googleReview: this.googleReviewLink,
          // At this point no real URLs for tenants
          officeWebsite: null,
          landingpagePerOffice: null,
        }

        this.maskedTargetLink = maskMap[target] ?? urlMap[target]
        const urlWithTracking =
          maskMap[target] ??
          buildUrlWithTracking(urlMap[target], this.kind, this.selectedImageSourceType)

        let mainText = text
        let footer = ''

        // this.company?.meta?.markAiGeneratedContent is the old flag for legacy reasons, we should remove it in the future
        const markedAiGeneratedText =
          this.company?.meta?.markAiGeneratedContent ||
          this.company?.meta?.markAiGeneratedTextContent

        if (markedAiGeneratedText) {
          const normalized = (text || '').trimEnd()
          const blocks = normalized.split(/\n\s*\n/)

          if (blocks.length > 1) {
            const lastBlock = blocks[blocks.length - 1].trim()

            const isShortFooter = lastBlock.length <= 80 && lastBlock.split('\n').length <= 2

            if (isShortFooter) {
              footer = lastBlock
              mainText = blocks.slice(0, -1).join('\n\n').trimEnd()
            }
          }
        }

        const hashtagIndex = mainText.indexOf('#')
        let result = ''

        if (hashtagIndex === -1) {
          result = mainText + '\n\n' + urlWithTracking
        } else {
          result =
            mainText.slice(0, hashtagIndex).trimEnd() +
            '\n\n' +
            urlWithTracking +
            '\n\n' +
            mainText.slice(hashtagIndex).trimStart()
        }

        if (footer) {
          result += '\n\n' + footer
        }

        return result
      }
      return text
    },
    updateLinkInText(text) {
      const url = this.topic?.landingpage?.url
      const regex = new RegExp(`${url}\\?utm_source=.*&utm_medium=.*&utm_content=.*`)
      return text.replace(regex, buildUrlWithTracking(url, this.kind, this.selectedImageSourceType))
    },
    removeLinkFromText(text) {
      // Remove any existing link from the text (including masked links and UTM links)
      const urlPattern = /(https?:\/\/[^\s]+(\?[^\s]+)?)/g
      const maskedLinkPattern = /<< .+ >>/g

      let cleanedText = text.replace(urlPattern, '').replace(maskedLinkPattern, '')

      // Clean up extra line breaks that may have been left behind
      cleanedText = cleanedText.replace(/\n{3,}/g, '\n\n').trim()

      return cleanedText
    },
    updateTextWithCurrentLink() {
      // Only update if we have generated text
      if (!this.generatedContent.text?.choices?.length) {
        return
      }

      // Update all generated text choices
      this.generatedContent.text.choices = this.generatedContent.text.choices.map((text) => {
        const textWithoutLink = this.removeLinkFromText(text)
        return this.addLinkToText(textWithoutLink)
      })

      // Update selected text if it exists
      if (this.selectedContent.text) {
        const textWithoutLink = this.removeLinkFromText(this.selectedContent.text)
        this.selectedContent.text = this.addLinkToText(textWithoutLink)
      }
    },
    async generate() {
      const prevText = this.generatedContent.text

      this.startLoading('text')
      this.resetGeneratedContent('text')

      let choices = await this.generateText()
      if (!choices) {
        choices = prevText.choices
      }

      this.generatedContent.text = { choices }
      this.setDefaultSelectedText()
      this.stopLoading('text')

      this.$tracking.event('Content Creator', 'Clicked', 'Generation')
    },
    async generateText() {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: GENERATE_TEXT,
          variables: {
            input: {
              topicId: this.topicId,
              textKind: this.kind,
              isMasterView: this.isMasterView,
            },
          },
          refetchQueries: ['getGenerationStats'],
        })

        if (data?.generatedText?.choices?.length > 0) {
          return data.generatedText.choices.map((text) => {
            const linkedText = this.addLinkToText(text)

            if (this.kind === KindType.newsletter) {
              return this.editCtaButton(linkedText)
            }

            return linkedText
          })
        }

        this.$tracking.event('Textual Content', 'Generated', this.kind)
      } catch (err) {
        const graphQLError = err.graphQLErrors[0]
        if (graphQLError?.extensions.code === 'GENERATION_LIMIT_REACHED') {
          showSnackbarMessage(
            'warning',
            this.$t('alerts.content-creator.generate-text.limit-reached', {
              limit: graphQLError.extensions.limit,
            })
          )
        } else {
          showSnackbarMessage('error', this.$t('alerts.content-creator.generate-text.error'))
        }
      }
    },

    editCtaButton(text) {
      const landingpage = this.topics.find((topic) => topic.id === this.topicId)?.landingpage
      if ((this.topic?.isSystemGenerated && landingpage?.slug) || !this.topic?.isSystemGenerated) {
        if (/<CtaButton\b[^>]*>/.test(text)) {
          const styledText = text.replace(
            /<CtaButton\b([^>]*)>/gi,
            `<CtaButton$1 style="
              background-color: ${this.primaryColor};
              color: #ffffff;
              padding: 10px;
              width: 100px;
              border: none;
              text-shadow: none;
              font-weight: bold;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            ">`
          )
          return styledText
        }
        return text
      } else {
        const textWithoutButton = text.replace(
          /[\n\r\s]*<CtaButton\b[^>]*>[\s\S]*?<\/CtaButton>[\n\r\s]*/gi,
          ''
        )
        return textWithoutButton || text
      }
    },

    async getImages() {
      const noImages =
        !partMapper[this.kind].includes('image') ||
        !this.topicId ||
        !this.kind ||
        !this.selectedImageSourceType
      if (noImages) {
        return
      }

      if (this.selectedImageSourceType === 'NO_IMAGE' && this.kind === KindType.newsletter) {
        this.generatedContent.image = {
          choices: [
            {
              id: 'noImage',
              selectAi: () => this.setSelectedImageSourceRaw(ImageType.AI),
              selectStock: () => this.setSelectedImageSourceRaw(ImageType.STOCK),
            },
          ],
        }
        this.setDefaultSelectedImage()
        return
      }

      // A text-only Google Business post has no image state at all — never fetch
      // images for it.
      if (this.selectedImageSourceType === 'NO_IMAGE' && googleBusinessKinds.includes(this.kind)) {
        this.resetGeneratedContent('image')
        return
      }

      this.startLoading('image')
      const imagesFunctionsMap = {
        MATERIAL: this.getMaterials,
        STOCK: this.getStockPhotos,
        USER: this.getUserImages,
        AI: this.getAiImages,
      }
      const imagesFunction = imagesFunctionsMap[this.selectedImageSourceType]
      const images = typeof imagesFunction === 'function' ? await imagesFunction() : []
      this.generatedContent.image = images
      this.setDefaultSelectedImage()
      this.stopLoading('image')
    },

    async getMaterials() {
      const { hasStaticMaterials, hasMaterials } = this.getFeature(
        this.featureNames.LANDINGPAGE
      ).config
      let materials = []

      if (hasStaticMaterials && this.topic?.landingpage?.slug) {
        const {
          data: { adMaterials },
        } = await this.$apollo.query({
          query: GET_AD_MATERIALS,
          variables: {
            input: {
              topicIds: this.topic.id ? [this.topic.id] : [],
              itemsPerPage: 100,
            },
          },
        })
        const adMaterialsList = adMaterials?.adMaterials || []
        materials = adMaterialsList.map((m) => ({
          id: m.id,
          type: 'BASIC_AD',
          displayName: m.displayName,
          files: [
            {
              file: { id: m.id, cdnUrl: m.url },
              previewFile: { cdnUrl: m.previewUrl },
            },
          ],
        }))
      } else if (hasMaterials && (this.topic.landingpage || this.topic.id)) {
        // A self-service AI topic has no landingpage; its materials carry a
        // direct topic reference instead (see customerTopicMaterialService.js).
        const {
          data: { materialsForContentCreator },
        } = await this.$apollo.query({
          query: GET_MATERIALS,
          variables: {
            input: this.topic.landingpage
              ? { landingpageId: this.topic.landingpage.id }
              : { topicId: this.topic.id },
          },
        })
        materials = materialsForContentCreator
      }

      const kindsSupportingVideos = [
        KindType.facebookPost,
        KindType.instagramPost,
        KindType.linkedinPost,
        KindType.whatsappStatus,
      ]
      const supportsVideo = kindsSupportingVideos.includes(this.kind)

      const materialsWithFiles = materials
        .flatMap((material) =>
          material.files.map((file) => {
            const isVideo = file.file.type?.startsWith('video/')
            return {
              id: material.id + '-' + file.file.id,
              fileId: file.file.id,
              url: file.file.cdnUrl,
              preview: setQueryParameters(file.previewFile.cdnUrl, {
                height: this.kind === KindType.whatsappStatus ? 700 : 500,
              }),
              type: file.file.type,
              isVideo,
              materialType: material.type,
            }
          })
        )
        .filter((material) => {
          if (material.isVideo && !supportsVideo) {
            return false
          }
          return true
        })

      materialsWithFiles.sort((a, b) => {
        const priority = (item) => {
          if (item.isVideo) return 2
          if (item.materialType === 'CAMPAIGN') return 1
          return 0
        }
        return priority(a) - priority(b)
      })

      if (materialsWithFiles.length === 0) {
        return {
          choices: [
            {
              id: 'noMaterials',
              selectAi: () => this.setSelectedImageSourceRaw(ImageType.AI),
              selectStock: () => this.setSelectedImageSourceRaw(ImageType.STOCK),
            },
          ],
        }
      }

      // If we have an imageId from query, sort it to be first in the list
      if (this.imageIdFromQuery) {
        materialsWithFiles.sort((a, b) =>
          a.id === this.imageIdFromQuery ? -1 : b.id === this.imageIdFromQuery ? 1 : 0
        )
      }

      return { choices: materialsWithFiles }
    },

    async getAiImages() {
      this.$tracking.event('Content Creator', 'Clicked', 'AI-Image Generation')
      const {
        data: { generatedImages },
      } = await this.$apollo.query({
        // Otherwise the function resolves with the cached data and doesn't wait for the network result
        fetchPolicy: 'no-cache',
        query: GET_GENERATED_IMAGES,
        variables: {
          input: {
            topicId: this.topicId,
            perPage: 50,
          },
        },
      })

      this.$tracking.event('Content Creator', 'Generated', 'AI-Image')

      const imageChoices = generatedImages.images.map(
        ({ url, id, fileId, history = [], originalImageUrl }) => ({
          preview: setQueryParameters(url, {
            height: this.kind === KindType.whatsappStatus ? 700 : 500,
          }),
          url,
          id,
          fileId,
          history,
          originalImageUrl,
        })
      )

      // If we have an imageId from query, sort it to be first in the list
      if (this.imageIdFromQuery) {
        imageChoices.sort((a, b) =>
          a.id === this.imageIdFromQuery ? -1 : b.id === this.imageIdFromQuery ? 1 : 0
        )
      }

      return {
        choices: [
          ...imageChoices,
          {
            id: 'fetchMore',
            text:
              generatedImages.total === 0 &&
              this.$t('content-creator.creator.generate-first-image'),
            button: this.$t('buttons.content-creator.generate-image'),
            fetchMore: () => this.generateImage(),
          },
        ],
      }
    },

    async getUserImages() {
      const {
        data: { userImages },
      } = await this.$apollo.query({
        // Otherwise the function resolves with the cached data and doesn't wait for the network result
        fetchPolicy: 'no-cache',
        query: GET_USER_IMAGES,
        variables: {
          input: {
            topicId: this.topicId,
          },
        },
      })

      // Post types that support videos
      const kindsSupportingVideos = [
        KindType.facebookPost,
        KindType.instagramPost,
        KindType.linkedinPost,
        KindType.whatsappStatus,
      ]
      const supportsVideo = kindsSupportingVideos.includes(this.kind)

      const userMediaChoices = userImages.images
        .map(({ url, id, fileId, history = [], originalImageUrl }) => {
          const isVideo = url?.match(/\.(mp4|mov|avi|wmv|webm)(\?|$)/i)
          return {
            preview: isVideo
              ? url
              : setQueryParameters(url, {
                  height: this.kind === KindType.whatsappStatus ? 700 : 500,
                }),
            url,
            id,
            fileId,
            history,
            originalImageUrl,
            type: isVideo ? 'video' : 'image',
            isVideo: !!isVideo,
          }
        })
        .filter((media) => {
          // Filter out videos if the current post type doesn't support them
          if (media.type === 'video' && !supportsVideo) {
            return false
          }
          return true
        })

      // If we have an imageId from query, sort it to be first in the list
      if (this.imageIdFromQuery) {
        userMediaChoices.sort((a, b) =>
          a.id === this.imageIdFromQuery ? -1 : b.id === this.imageIdFromQuery ? 1 : 0
        )
      }

      const uploadOptions = [
        {
          id: 'uploadImage',
          fetchMore: (file) => this.uploadMedia(file),
        },
      ]

      // Only show video upload option for post types that support videos
      if (supportsVideo) {
        uploadOptions.push({
          id: 'uploadVideo',
          fetchMore: (file) => this.uploadMedia(file),
        })
      }

      return {
        choices: [...userMediaChoices, ...uploadOptions],
      }
    },
    async uploadMedia(files) {
      const uploadFiles = Array.isArray(files) ? files : [files]
      this.startLoading('image')
      const isVideo = uploadFiles.some((f) => f.type.startsWith('video/'))
      if (isVideo && !this.$auth?.user?.companyId && import.meta.env.VITE_APP_STAGE === 'local') {
        showSnackbarMessage('error', this.$t('ad-materials.upload.error-no-company'))
        this.stopLoading('image')
        return
      }
      let materialId = null
      try {
        const uploadOne = async (file) => {
          const fileIsVideo = file.type.startsWith('video/')
          if (import.meta.env.VITE_APP_STAGE !== 'local') {
            if (fileIsVideo) {
              const urlRes = await this.$apollo.mutate({
                mutation: REQUEST_MATERIAL_UPLOAD_URL,
                variables: {
                  input: {
                    topicId: this.topicId,
                    mimeType: file.type,
                    filename: file.name,
                    fileSize: file.size,
                    assetType: 'VIDEO',
                  },
                },
              })
              const { uploadUrl, s3Key } = urlRes.data.requestMaterialUploadUrl
              await uploadToS3(uploadUrl, file, file.type)
              return this.$apollo.mutate({
                mutation: CONFIRM_MATERIAL_UPLOAD,
                variables: {
                  input: {
                    s3Key,
                    topicId: this.topicId,
                    mimeType: file.type,
                    filename: file.name,
                    assetType: 'VIDEO',
                  },
                },
              })
            }
            const urlRes = await this.$apollo.mutate({
              mutation: REQUEST_USER_IMAGE_UPLOAD_URL,
              variables: { input: { mimeType: file.type } },
            })
            const { uploadUrl, s3Key } = urlRes.data.requestUserImageUploadUrl
            await uploadToS3(uploadUrl, file, file.type)
            return this.$apollo.mutate({
              mutation: CONFIRM_USER_IMAGE_UPLOAD,
              variables: { input: { topicId: this.topicId, s3Key, mimeType: file.type } },
            })
          }
          if (fileIsVideo) {
            return this.$apollo.mutate({
              mutation: UPLOAD_MATERIALS,
              variables: {
                input: {
                  file,
                  topicId: this.topicId,
                  previewFile: file,
                  companyId: this.$auth.user.companyId,
                  materialId: '',
                  assetType: 'VIDEO',
                },
              },
            })
          }
          return this.$apollo.mutate({
            mutation: UPLOAD_USER_IMAGE,
            variables: { input: { topicId: this.topicId, image: file } },
          })
        }
        const [firstUpload] = await Promise.all(uploadFiles.map(uploadOne))
        if (firstUpload.data?.uploadUserTopicMaterial?.previewFile?.id) {
          materialId = firstUpload.data.uploadUserTopicMaterial.previewFile.id
        } else if (firstUpload.data?.confirmMaterialUpload?.id) {
          materialId = firstUpload.data.confirmMaterialUpload.id
        } else if (firstUpload.data?.userImage?.fileId) {
          materialId = firstUpload.data.userImage.fileId
        }
      } catch (err) {
        Sentry.addBreadcrumb({
          category: isVideo ? 'content-creator.video-upload' : 'content-creator.image-upload',
          level: 'error',
          message: `Could not upload user ${isVideo ? 'video' : 'image'}`,
          data: {
            topicId: this.topicId,
            imageSourceType: this.selectedImageSourceType,
            fileCount: Array.isArray(uploadFiles) ? uploadFiles.length : 1,
          },
        })
        Sentry.captureException(err, {
          level: 'error',
          tags: {
            module: 'content-creator',
            action: isVideo ? 'upload-video' : 'upload-image',
            imageSourceType: this.selectedImageSourceType,
          },
          extra: {
            topicId: this.topicId,
            graphQLErrors: err?.graphQLErrors,
            networkError: err?.networkError?.result || err?.networkError?.message,
          },
        })
        const errorKey = isVideo
          ? 'alerts.content-creator.upload-video.error'
          : 'alerts.content-creator.upload-image.error'
        showSnackbarMessage('error', this.$t(errorKey))
      }
      this.stopLoading('image')
      this.generatedContent.image = await this.getUserImages()
      this.imageIdFromQuery = materialId
      this.setDefaultSelectedImage()
    },
    async generateImage() {
      this.startLoading('image')
      this.$tracking.event('Content Creator', 'Clicked', 'AI-Image Generation')
      try {
        const aspectRatio = this.selectedKindAspectRatios[0]
        await this.$apollo.mutate({
          mutation: GENERATE_IMAGE,
          variables: {
            input: {
              topicId: this.topicId,
              modelName: this.selectedImageSourceVariant,
              type: this.kind,
              aspectRatio,
              isMasterView: this.isMasterView,
            },
          },
          refetchQueries: ['getGenerationStats'],
        })
      } catch (err) {
        const graphQLError = err.graphQLErrors[0]
        if (graphQLError?.extensions.code === 'GENERATION_LIMIT_REACHED') {
          showSnackbarMessage(
            'warning',
            this.$t('alerts.content-creator.generate-ai-image.limit-reached', {
              limit: graphQLError.extensions.limit,
            })
          )
        } else {
          showSnackbarMessage('error', this.$t('alerts.content-creator.generate-ai-image.error'))
        }
      }
      this.stopLoading('image')
      this.generatedContent.image = await this.getAiImages()
      this.setDefaultSelectedImage()
    },

    async getStockPhotos() {
      this.$tracking.event('Content Creator', 'Clicked', 'Stockphoto Download')
      this.stockPhotos.page = 1
      this.isStockPhotosReady = false
      const images = await this.fetchStockPhotos()

      // If we have an imageId from query, sort it to be first in the list
      if (this.imageIdFromQuery) {
        images.sort((a, b) =>
          a.id === this.imageIdFromQuery ? -1 : b.id === this.imageIdFromQuery ? 1 : 0
        )
      }

      this.isStockPhotosReady = true
      return {
        choices: [
          ...images,
          {
            id: 'fetchMore',
            button: this.$t('buttons.content-creator.load-more'),
            fetchMore: () => this.getMoreStockPhotos(),
          },
        ],
      }
    },
    async getMoreStockPhotos() {
      if (this.stockPhotos.page * this.stockPhotos.perPage >= this.stockPhotos.limit) {
        showSnackbarMessage(
          'info',
          this.$t('alerts.content-creator.generate-stock-image.limit-reached', {
            limit: this.stockPhotos.limit,
          })
        )
        return {}
      }
      this.stockPhotos.page++
      const images = await this.fetchStockPhotos()
      this.generatedContent.image = {
        choices: [...images, ...this.generatedContent.image.choices],
      }
    },
    async fetchStockPhotos() {
      this.startLoading('image')
      const {
        data: { stockPhotos },
      } = await this.$apollo.query({
        query: GET_STOCK_PHOTOS,
        variables: {
          input: {
            provider: this.selectedImageSourceVariant,
            topic: this.topicId,
            page: this.stockPhotos.page,
            per_page: this.stockPhotos.perPage,
          },
        },
      })
      this.stopLoading('image')

      this.$tracking.event('Content Creator', 'Loaded', 'Stockphoto')

      if (stockPhotos.photos.length === 0) {
        showSnackbarMessage('info', this.$t('alerts.content-creator.generate-stock-image.info'))
        return []
      }
      return stockPhotos.photos.map(
        ({ src, canBeDownloaded, isLicenseRequired, id, fileId, history }) => ({
          preview: setQueryParameters(src.original, {
            h: 500,
            w: 500,
            auto: 'compress',
            fit: 'crop',
          }),
          url: setQueryParameters(src.original, {
            h: 1500,
            w: 1500,
            auto: 'compress',
            fit: 'crop',
          }),
          canBeDownloaded,
          isLicenseRequired,
          id,
          fileId,
          history,
          provider: this.selectedImageSourceVariant,
        })
      )
    },
    openSocialMediaDialog() {
      this.showSocialMediaDialog = true
    },

    goToUpgrade() {
      this.$tracking.event('Content Creator', 'Clicked', 'Upgrade', 'Textkind')
      if (this.isPlatformSmg) {
        window.open(this.upgradePackageUrl, '_blank')
        return
      }

      this.$router.push({
        name: 'Settings',
        params: { section: 'contract' },
        query: { showUpgrade: '1' },
      })
    },

    /**
     * Sets the default image source on this.selectedImageSourceRaw attribute.
     * If type is provided it will be used to lookup for a default variant.
     * Otherwise, it will use the default image source from the feature config.
     *
     * @param {string} [type=null] - The type of the image source. If not provided, defaults to null.
     */
    setSelectedImageSourceRaw(type = null) {
      if (!type) {
        type = this.featureConfig.defaultImageSource
      }
      const defaultConf = this.availableImageSources.find((c) => c.value === type)
      const variant = defaultConf?.defaultVariant || defaultConf?.variants?.[0]?.value

      this.selectedImageSourceRaw = variant ? `${type}-${variant}` : type
    },

    addImageToCarousel(image) {
      if (this.selectedCarouselImages.length === 20) return
      if (!this.selectedCarouselImages.find((img) => img.id === image.id)) {
        this.selectedCarouselImages.push(image)
      }
    },

    removeImageFromCarousel(imageId) {
      this.selectedCarouselImages = this.selectedCarouselImages.filter((img) => img.id !== imageId)
    },

    handleCarouselImageToggle(image) {
      // Only allow carousel for Facebook and Instagram posts
      const carouselKinds = [KindType.facebookPost, KindType.instagramPost]
      if (!carouselKinds.includes(this.kind)) {
        return
      }
      const isSelected = this.selectedCarouselImages.find((img) => img.id === image.id)
      if (isSelected) {
        this.removeImageFromCarousel(image.id)
      } else {
        this.addImageToCarousel(image)
      }
    },
  },
  apollo: {
    googleReviewSettings: {
      query: GET_GOOGLE_REVIEW_SETTINGS,
      variables() {
        return {
          companyId: this.$auth.user.companyId,
        }
      },
      result({ data }) {
        this.googleReviewLink = data.settings?.company?.googleReviewLink ?? null
      },
    },
    returnCallLandingpage: {
      query: RETURN_CALL_LANDINGPAGE,
      variables() {
        return {
          input: {
            landingpageType: 'RETURN_CALL',
          },
        }
      },
      skip() {
        return this.topic?.isSystemGenerated
      },
    },
    topics: {
      query: GET_ACTIVE_TOPICS,
    },
    lastGeneratedText: {
      query: GET_LAST_GENERATED_TEXT,
      variables() {
        return {
          input: {
            topicId: this.topicId,
            textKind: this.kind,
          },
        }
      },
      skip() {
        return !this.topic?.id
      },
      // Using cache caused issues with re-generation of content. `update` or `result` is called once for the cached data
      // and once again for the network result. Unfortunately if cached data is empty (and network result is not)
      // generation of new content is unnecessarily triggered. I think in this case it's fine using only the cache
      // as I don't seee the case where one would switch text kind or topic forth and back during the same session.
      fetchPolicy: 'network-only',
      result({ data }) {
        if (data.lastGeneratedText?.choices?.length > 0) {
          this.generatedContent.text = {
            choices: data.lastGeneratedText.choices.map((text) => {
              const linkedText = this.addLinkToText(text)

              if (this.kind === KindType.newsletter) {
                return this.editCtaButton(linkedText)
              }

              return linkedText
            }),
          }
          this.setDefaultSelectedText()
        }
      },
    },
    company: {
      query: COMPANY,
      variables() {
        return {
          id: this.$auth.user.companyId,
        }
      },
    },
    socialMediaConnections: {
      query: GET_SOCIAL_MEDIA_CONNECTIONS,
      skip() {
        return !this.isMasterView
      },
      update({ socialMediaConnections } = { socialMediaConnections: [] }) {
        return socialMediaConnections || []
      },
    },
  },
}
</script>

<style scoped>
.creator-card {
  overflow: unset;
}
@media (max-width: 960px) {
  .generate-btn {
    margin-left: 0;
    margin-right: 0;
  }
  .generate-btn,
  .social-media-btn {
    width: 100%;
  }
}

.pointer {
  cursor: pointer;
}

.needs-upgrade-subtitle {
  font-weight: normal !important;
}

.disabled-subtitle {
  font-weight: normal !important;
}

:deep(fieldset) {
  min-width: 64px;
  padding: 0 16px;
  border-radius: 4px;
  border-color: rgba(0, 0, 0, 0.12);
}

:deep(.v-label.v-field-label--floating) {
  font-weight: 600;
  opacity: 1;
}

.v-btn:not(.v-btn--round).v-size--default {
  height: 40px;
}
</style>
